import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  CheckCircle as ConfirmedIcon,
  Error as FailedIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import CopyIconButton from '../../../shared/components/CopyIconButton';
import { useEventTrace } from '../hooks/useEventTrace';
import type { TraceStage, TraceStatus } from '../../../types/frontend';
import type { LiveEvent } from '../../../types/backend';

interface EventTraceTimelineProps {
  customerId: string;
  event: LiveEvent;
}

const STATUS_COLORS: Record<TraceStatus, 'success' | 'error' | 'info' | 'grey' | 'warning'> = {
  confirmed: 'success',
  failed: 'error',
  inferred: 'success',
  degraded: 'warning',
  unmatched: 'info',
  no_data: 'grey',
};

const OVERALL: Record<TraceStatus, { label: string; color: string }> = {
  confirmed: { label: 'OK', color: 'success.main' },
  inferred: { label: 'OK', color: 'success.main' },
  unmatched: { label: 'No rule matched', color: 'info.main' },
  degraded: { label: 'Degraded', color: 'warning.main' },
  failed: { label: 'Failed', color: 'error.main' },
  no_data: { label: 'No data', color: 'text.disabled' },
};

const IdRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Typography variant="caption" noWrap sx={{ fontFamily: 'monospace', color: 'text.secondary', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {value}
    </Typography>
    <CopyIconButton text={value} iconSize={13} sx={{ p: 0.25, flexShrink: 0 }} />
  </Box>
);

const getStageIcon = (status: TraceStatus) => {
  if (status === 'confirmed') return <ConfirmedIcon fontSize="small" />;
  if (status === 'failed') return <FailedIcon fontSize="small" />;
  return <DotIcon fontSize="small" />;
};

const formatDelta = (deltaMs: number | null): string => {
  if (deltaMs === null || deltaMs === undefined) return '';
  if (deltaMs < 1000) return `+${deltaMs}ms`;
  if (deltaMs < 60000) return `+${(deltaMs / 1000).toFixed(1)}s`;
  return `+${(deltaMs / 60000).toFixed(1)}m`;
};

const EventTraceTimeline: React.FC<EventTraceTimelineProps> = ({ customerId, event }) => {
  const { trace, loading, error } = useEventTrace(customerId, event);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <InlineAlert severity="error" sx={{ mb: 1 }}>{error}</InlineAlert>;
  }

  if (!trace) return null;

  const overall = OVERALL[trace.overall_status] ?? { label: trace.overall_status, color: 'text.secondary' };

  return (
    <Box>
      {/* Outcome + identifiers, as plain data */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
        <DotIcon sx={{ fontSize: 11, color: overall.color }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{overall.label}</Typography>
        <Typography variant="caption" color="text.secondary">
          · {trace.path === 'forwarder' ? 'forwarder path' : 'direct path'}
        </Typography>
      </Box>

      <IdRow label="Event ID" value={trace.event_id} />
      {trace.xray_trace_id && <IdRow label="X-Ray" value={trace.xray_trace_id} />}

      <Timeline position="right" sx={{ p: 0, m: 0, mt: 1 }}>
        {trace.stages.map((stage: TraceStage, index: number) => {
          const isLast = index === trace.stages.length - 1;
          return (
            <TimelineItem key={stage.stage} sx={{ minHeight: 52, '&:before': { flex: 0, p: 0 } }}>
              <TimelineOppositeContent sx={{ flex: 0.28, py: '6px', px: 1 }}>
                {stage.timestamp && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
                    {new Date(stage.timestamp).toLocaleTimeString()}
                  </Typography>
                )}
                {stage.delta_ms !== null && stage.delta_ms !== undefined && (
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontSize: 10 }}>
                    {formatDelta(stage.delta_ms)}
                  </Typography>
                )}
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot color={STATUS_COLORS[stage.status]} sx={{ p: 0.4, my: 0 }}>
                  {getStageIcon(stage.status)}
                </TimelineDot>
                {!isLast && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ py: '6px', px: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{stage.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>
                  {stage.account_label}{stage.evidence ? ` · ${stage.evidence}` : ''}
                </Typography>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    </Box>
  );
};

export default EventTraceTimeline;
